import { useOutletContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Grid from '../../components/grid/Grid';
import { getAuditLogsForDevice } from '../../services/AuditLogs';
import { formatToBrowserTime, formatToUtcIso } from '../../utils/timeFormat';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import DateRange from '../../components/dateRange/DateRange';
import { DEFAULT_DAYS_AUDITLOGS_DATA } from '../../utils/constants';
import dayjs from "dayjs";
import AuditPagination from '../../components/auditPagination/AuditPagination';
import './DeviceAuditLogs.css'
import { toast } from 'react-toastify';

const DEFAULT_RANGE_DAYS = DEFAULT_DAYS_AUDITLOGS_DATA;
const DEFAULT_LIMIT = 100;

const DeviceAuditLogs = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const ctx = useOutletContext?.() || {};
    const qs = new URLSearchParams(location.search);
    const model = ctx?.model ?? qs.get('model') ?? '';
    const serialNumber = ctx?.serialNumber ?? qs.get('serialNumber') ?? '';

    const nowFloored = dayjs();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState(() =>
        nowFloored.subtract(DEFAULT_RANGE_DAYS, 'day')
    );
    const [endDate, setEndDate] = useState(() => nowFloored);
    const [rangeError, setRangeError] = useState('');

    // pagination-related states
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [currentCursor, setCurrentCursor] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [totalItems, setTotalItems] = useState(null);
    const [lastSearchParams, setLastSearchParams] = useState(null);

    const headers = [
        { key: 'id', label: 'ID', visible: false },
        { key: 'PK', label: 'Asset Name', visible: false },
        { key: 'audit_category', label: 'Category', width: '15%' },
        { key: 'message', label: 'Message', width: '45%' },
        { key: 'context_type', label: 'Context', width: '10%' },
        { key: 'user_name', label: 'Username', width: '10%' },
        { key: 'created_date', label: 'Audit Time', width: '20%' },
    ];

    const buildDefaultSearch = (s = startDate, e = endDate) => {
        const now = dayjs();
        const start = dayjs(s);
        let end = dayjs(e);
        if (end.isAfter(now)) end = now;

        return {
            device_name: `${model}-${serialNumber}`,
            limit: DEFAULT_LIMIT,
            start_date: formatToUtcIso(start, false),
            end_date: formatToUtcIso(end, false),
        };
    };

    const mapApiResponseToGridData = (dataArray) => {
        return (dataArray ?? []).map((item, index) => ({
            id: index + 1,
            PK: item?.PK ?? '',
            audit_category: item?.audit_category ?? '',
            message: item?.message ?? '',
            context_type: item?.context_type ?? '',
            user_name: item?.user_name ?? '',
            created_date: item?.created_date
                ? formatToBrowserTime(item.created_date, false, true)
                : ''
        }));
    };

    const normalizeToArray = (input) => {
        if (!input) return [];
        if (Array.isArray(input)) return input;
        return [input];
    };
    const loadData = async (searchData, cursor = null, replacePagination = false) => {
        try {
            setLoading(true);

            const params = { ...searchData, limit: searchData.limit ?? limit };
            if (cursor) params.lastKey = cursor;

            const raw = await getAuditLogsForDevice(params);

            const list = Array.isArray(raw?.items)
                ? raw.items
                : normalizeToArray(raw?.items ?? raw);
                
            const mapped = mapApiResponseToGridData(list);
            setData(mapped);

            // Only update totalItems when server explicitly provides it (prevents wiping)
            if (typeof raw?.totalItems === 'number') {
                setTotalItems(raw.totalItems);
            }

            setCurrentCursor(cursor ?? null);
            setNextCursor(raw?.lastKey ?? null);

            if (replacePagination) {
                setLastSearchParams(params);
            }
        } catch (err) {
            console.error('Fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    // initial load: set lastSearchParams before calling loadData (consistent with GlobalAuditLogs)
    useEffect(() => {
        if (!model || !serialNumber) {
            setLoading(false);
            toast.error('Missing model or serial number.');
            return;
        }

        const initialSearch = buildDefaultSearch();
        setLimit(initialSearch.limit);
        setLastSearchParams(initialSearch);
        loadData(initialSearch, null, true);
    }, [model, serialNumber]);

    const handleSearch = () => {
        if (rangeError) return;
        const sd = buildDefaultSearch(startDate, endDate);
        sd.limit = DEFAULT_LIMIT;
        setLimit(sd.limit);
        setCurrentCursor(null);
        setNextCursor(null);
        setTotalItems(null);
        setLastSearchParams(sd);
        loadData(sd, null, true);
    };

    const handleReset = () => {
        const now = dayjs();
        const start = now.subtract(DEFAULT_RANGE_DAYS, 'day');
        setStartDate(start);
        setEndDate(now);
        setRangeError('');

        const sd = {
            device_name: `${model}-${serialNumber}`,
            limit: DEFAULT_LIMIT,
            start_date: formatToUtcIso(start, false),
            end_date: formatToUtcIso(now, false)
        };

        setLimit(sd.limit);
        setCurrentCursor(null);
        setNextCursor(null);
        setTotalItems(null);
        setLastSearchParams(sd);
        loadData(sd, null, true);
    };

    const handleNavigate = (cursor, searchData) => {
        const sd = searchData ?? lastSearchParams ?? buildDefaultSearch(startDate, endDate);
        sd.limit = sd.limit ?? limit ?? DEFAULT_LIMIT;
        loadData(sd, cursor, false);
    };

    return (
        <div className='dAudit-page-wrapper'>
            <div className="dAudit-header ">
                <DateRange
                    defaultDays={DEFAULT_RANGE_DAYS}
                    startTime={startDate}
                    endTime={endDate}
                    onChange={({ startDate: s, endDate: e, rangeError: err }) => {
                        setStartDate(s);
                        setEndDate(e);
                        setRangeError(err);
                    }}
                />
                <button
                    className="btn btn-primary"
                    onClick={handleSearch}
                    disabled={loading || !!rangeError}
                    title={rangeError || undefined}
                >
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    {t('Search') || 'Search'}
                </button>
                <button className="btn btn-primary" onClick={handleReset}>
                    <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                    {t('Reset') || 'Reset'}
                </button>
            </div>
            <div className='dAudit-content'>
                <Grid
                    headers={headers}
                    data={data}
                    rowsPerPage={limit}
                    canMultiSelect={false}
                    loading={loading}
                    sortable={true}
                    showPagination={false}
                />
            </div>

            <AuditPagination
                currentCursor={currentCursor}
                nextCursor={nextCursor}
                onNavigate={handleNavigate}
                loading={loading}
                totalItems={totalItems}
                limit={limit}
                searchData={lastSearchParams}
            />
        </div>
    );
};

export default DeviceAuditLogs;
